import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  InputBase,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Title } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

interface Country {
  countryId: number;
  nameEn: string;
  nameRu?: string;
  nameCh?: string;
}

const emojis = ['🇺🇸', '🇬🇧', '🇩🇪', '🇫🇷', '🇪🇸', '🇮🇹', '🇷🇺', '🇨🇳', '🇯🇵', '🇰🇷', '🇧🇷', '🇮🇳', '🇹🇷', '🇳🇱', '🇵🇱'];
const getEmoji = (id: number) => emojis[id % emojis.length];

const renderCountryIcon = (id: number, size: number = 18) => (
  <Avatar sx={{ width: size + 16, height: size + 16, backgroundColor: 'rgba(255, 159, 67, 0.12)', fontSize: '1.1rem' }}>
    {getEmoji(id)}
  </Avatar>
);

const ExcludedCountriesPage = () => {
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [excludedIds, setExcludedIds] = useState<number[]>([]);
  const [savedIds, setSavedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [countriesRes, excludedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/sms/countries`, { headers }),
        fetch(`${API_URL}/api/admin/settings/excluded-countries/list`, { headers }),
      ]);

      const countriesData = await countriesRes.json();
      const excludedData = await excludedRes.json();

      if (countriesData.data) {
        setAllCountries(countriesData.data);
      }
      const ids = (excludedData.excludedCountries || []).map(Number);
      setExcludedIds(ids);
      setSavedIds(ids);
    } catch {
      setSnackbar({ open: true, message: 'Veriler yüklenemedi', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = (id: number) => {
    if (!excludedIds.includes(id)) {
      setExcludedIds([...excludedIds, id]);
    }
  };

  const handleRemove = (id: number) => {
    setExcludedIds(excludedIds.filter((c) => c !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/settings/excluded-countries/list`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ excludedCountries: excludedIds }),
      });
      if (!res.ok) throw new Error();
      setSavedIds([...excludedIds]);
      setSnackbar({ open: true, message: 'Hariç tutulan ülkeler kaydedildi', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kaydetme hatası', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify([...excludedIds].sort()) !== JSON.stringify([...savedIds].sort());

  const excludedCountries = excludedIds
    .map((id) => allCountries.find((c) => c.countryId === id) || { countryId: id, nameEn: `ID: ${id}` })
    .filter(Boolean) as Country[];

  const availableCountries = allCountries.filter(
    (c) => !excludedIds.includes(c.countryId) &&
      (searchTerm === '' ||
        c.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(c.countryId).includes(searchTerm))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#7367f0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Hariç Tutulan Ülkeler" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BlockIcon sx={{ color: '#ea5455', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
              Hariç Tutulan Ülkeler
            </Typography>
            <Typography variant="body2" sx={{ color: '#676d7d' }}>
              Mobil uygulamada gizlenecek ülkeleri yönetin
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ borderColor: '#404656', color: '#d0d2d6', '&:hover': { borderColor: '#7367f0' } }}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges}
            sx={{
              backgroundColor: '#7367f0',
              '&:hover': { backgroundColor: '#5e50ee' },
              '&.Mui-disabled': { backgroundColor: '#404656', color: '#676d7d' },
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>
      </Box>

      {/* Selected Excluded Countries */}
      <Card sx={{ mb: 3, backgroundColor: '#283046', border: '1px solid #404656' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d0d2d6' }}>
              Gizlenen Ülkeler ({excludedCountries.length})
            </Typography>
            {hasChanges && (
              <Chip
                label="Kaydedilmemiş değişiklikler var"
                size="small"
                sx={{ backgroundColor: 'rgba(255, 159, 67, 0.16)', color: '#ff9f43', fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {excludedCountries.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <BlockIcon sx={{ fontSize: 40, color: '#404656', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                Henüz hariç tutulan ülke yok. Aşağıdan ülke ekleyebilirsiniz.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {excludedCountries.map((country) => (
                <Card
                  key={country.countryId}
                  sx={{
                    backgroundColor: '#161d31',
                    border: '1px solid #404656',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#ea5455' },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {renderCountryIcon(country.countryId, 18)}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d2d6', lineHeight: 1.2 }}>
                        {country.nameEn}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#676d7d' }}>
                        ID: {country.countryId}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(country.countryId)}
                      sx={{ ml: 1, color: '#ea5455', '&:hover': { backgroundColor: 'rgba(234, 84, 85, 0.12)' } }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Search & Add Countries */}
      <Card sx={{ backgroundColor: '#283046', border: '1px solid #404656' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d0d2d6', mb: 2 }}>
            Ülke Gizle
          </Typography>

          {/* Search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              mb: 2,
              borderRadius: 1,
              backgroundColor: '#161d31',
              border: '1px solid #404656',
            }}
          >
            <SearchIcon sx={{ color: '#676d7d', fontSize: 20, mr: 1.5 }} />
            <InputBase
              placeholder="Ülke adı veya ID ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                color: '#d0d2d6',
                fontSize: '0.875rem',
                flex: 1,
                '& ::placeholder': { color: '#676d7d', opacity: 1 },
              }}
            />
            {searchTerm && (
              <Chip
                label={`${availableCountries.length} sonuç`}
                size="small"
                sx={{ backgroundColor: 'rgba(115, 103, 240, 0.12)', color: '#7367f0', fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>

          {/* Available Countries Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 1,
              maxHeight: 400,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#404656', borderRadius: 3 },
            }}
          >
            {availableCountries.map((country) => (
              <Box
                key={country.countryId}
                onClick={() => handleAdd(country.countryId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { backgroundColor: 'rgba(234, 84, 85, 0.08)' },
                }}
              >
                {renderCountryIcon(country.countryId, 16)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#d0d2d6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                  >
                    {country.nameEn}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#676d7d', fontSize: '0.65rem' }}>
                    ID: {country.countryId}
                  </Typography>
                </Box>
                <AddIcon sx={{ color: '#ea5455', fontSize: 18 }} />
              </Box>
            ))}
          </Box>

          {availableCountries.length === 0 && searchTerm && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                "{searchTerm}" ile eşleşen ülke bulunamadı
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExcludedCountriesPage;
